import type { SelectOption } from './useSelectState';

/**
 * Normalize an incoming multiple-mode value into a string array. Tolerates a
 * single string (defensive), null/undefined, or an already-array value.
 */
export function toValueArray(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw.filter((v): v is string => typeof v === 'string');
  if (typeof raw === 'string' && raw) return [raw];
  return [];
}

/**
 * Removes diacritics from a string for accent-insensitive matching.
 *
 * @param str The input string.
 * @returns The string with combining accent marks stripped.
 */
export function stripAccents(str: string): string {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/**
 * Decides whether the query text should be synced to the selected option's
 * expected display name (only when blurred, something is selected, and drifted).
 *
 * @param focused Whether the input is currently focused.
 * @param selected The selected option value.
 * @param currentQuery The current query text.
 * @param expected The expected display name for the selection.
 * @returns `true` when the query should be re-synced.
 */
export function shouldSyncQuery(
  focused: boolean,
  selected: string,
  currentQuery: string,
  expected: string,
): boolean {
  if (focused || !selected) return false;
  return currentQuery !== expected && currentQuery !== '';
}

/**
 * Determines whether a single exact-match option should be auto-selected as the
 * user types (one match, not already selected, and the query equals it).
 *
 * @param filtered The currently filtered option values.
 * @param currentQuery The current query text.
 * @param selected The already-selected option value.
 * @returns `true` when auto-selection should occur.
 */
export function shouldAutoSelect(filtered: string[], currentQuery: string, selected: string): boolean {
  const hasSingleMatch = filtered.length === 1 && !!currentQuery;
  const isNewSelection = selected !== filtered[0];
  const queryMatchesOption = currentQuery === filtered[0];
  return hasSingleMatch && isNewSelection && queryMatchesOption;
}

/**
 * Reports whether the input is focused with an empty query (so the displayed
 * value should be blanked to let the user type freely).
 *
 * @param focused Whether the input is focused.
 * @param currentQuery The current query text.
 * @returns `true` when focused and empty.
 */
function isEmptyFocused(focused: boolean, currentQuery: string): boolean {
  return focused && currentQuery === '';
}

/**
 * Computes the value shown in the input, reconciling the raw query with the
 * selected option's expected display name and focus state.
 *
 * @param selected The selected option value.
 * @param currentQuery The current query text.
 * @param focused Whether the input is focused.
 * @param expected The expected display name for the selection.
 * @returns The string to render in the input.
 */
export function computeInputValue(
  selected: string,
  currentQuery: string,
  focused: boolean,
  expected: string,
): string {
  if (!selected) return currentQuery;
  if (isEmptyFocused(focused, currentQuery)) return '';
  const queryMatchesExpected = !currentQuery || currentQuery === expected;
  return queryMatchesExpected ? expected : currentQuery;
}

interface FilterParams {
  searchable: boolean;
  searchTerm: string;
  useTriggerMode: boolean;
  query: string;
  options: string[];
  normalizedData: SelectOption[];
  filterFunction?: (query: string, options: string[]) => string[];
  maxResults: number;
}

/**
 * Filters option values by an accent-insensitive substring match against their
 * display labels.
 *
 * @param normalizedData Option value/label pairs used to resolve labels.
 * @param options The candidate option values.
 * @param searchTerm The user's search text.
 * @returns The subset of option values whose labels match.
 */
function filterBySearchTerm(normalizedData: SelectOption[], options: string[], searchTerm: string): string[] {
  const needle = stripAccents(searchTerm.toLowerCase());
  return options.filter((opt) => {
    const label = normalizedData.find((d) => d.value === opt)?.label ?? opt;
    return stripAccents(label.toLowerCase()).includes(needle);
  });
}

/**
 * Filters option values by the query, matching an accent-insensitive substring
 * or a 1-based numeric index, capped at `maxResults`.
 *
 * @param query The current query text.
 * @param options The candidate option values.
 * @param maxResults Maximum number of results to return.
 * @returns The matching, capped option values.
 */
function filterOptionsByQuery(query: string, options: string[], maxResults: number): string[] {
  if (!query) return options.slice(0, maxResults);
  const needle = stripAccents(query.toLowerCase());
  return options.filter((option) => {
    const haystack = stripAccents(option.toLowerCase());
    return haystack.includes(needle) || (!isNaN(+query) && options.indexOf(option) + 1 === +query);
  }).slice(0, maxResults);
}

/**
 * Resolves the option list for non-searchable mode: filtered by an external
 * search term when present, otherwise the full option list.
 *
 * @param searchTerm An externally supplied search term.
 * @param normalizedData Option value/label pairs.
 * @param options The full option values.
 * @returns The resolved option values.
 */
function resolveNonSearchable(searchTerm: string, normalizedData: SelectOption[], options: string[]): string[] {
  return searchTerm ? filterBySearchTerm(normalizedData, options, searchTerm) : options;
}

/**
 * Computes the visible option values, dispatching across non-searchable,
 * trigger-mode, custom-filter, and default query-filter paths.
 *
 * @param p Bundled searchable flags, query/term, options, and filter config.
 * @returns The filtered option values.
 */
export function computeFilteredOptions(p: FilterParams): string[] {
  const { searchable, searchTerm, useTriggerMode, query, options, normalizedData, filterFunction, maxResults } = p;
  if (!searchable) return resolveNonSearchable(searchTerm, normalizedData, options);
  if (useTriggerMode && !searchTerm) return options;
  if (filterFunction) return filterFunction(query, options);
  return filterOptionsByQuery(query, options, maxResults);
}

/**
 * Filters normalized option data by the query, matching an accent-insensitive
 * label substring or a 1-based numeric index, capped at `maxResults`.
 *
 * @param query The current query text.
 * @param normalizedData The option value/label pairs to filter.
 * @param maxResults Maximum number of results to return.
 * @returns The matching, capped option data.
 */
function filterDataByQuery(query: string, normalizedData: SelectOption[], maxResults: number): SelectOption[] {
  if (!query) return normalizedData.slice(0, maxResults);
  const needle = stripAccents(query.toLowerCase());
  return normalizedData.filter((option) => {
    const haystack = stripAccents(option.label.toLowerCase());
    return haystack.includes(needle) || (!isNaN(+query) && normalizedData.indexOf(option) + 1 === +query);
  }).slice(0, maxResults);
}

/**
 * Resolves an external search term against normalized data by label substring.
 *
 * @param searchTerm The external search term.
 * @param normalizedData The option value/label pairs.
 * @returns The matching option data.
 */
function filterDataBySearchTerm(searchTerm: string, normalizedData: SelectOption[]): SelectOption[] {
  const needle = stripAccents(searchTerm.toLowerCase());
  return normalizedData.filter((option) => stripAccents(option.label.toLowerCase()).includes(needle));
}

/**
 * Resolves normalized data through a custom filter function by mapping the
 * filtered values back to their option data entries.
 *
 * @param filterFunction The custom filter over option values.
 * @param query The current query text.
 * @param options The candidate option values.
 * @param normalizedData The option value/label pairs.
 * @returns The option data whose values survived the filter.
 */
function filterDataByCustom(
  filterFunction: (query: string, options: string[]) => string[],
  query: string,
  options: string[],
  normalizedData: SelectOption[],
): SelectOption[] {
  const filteredVals = filterFunction(query, options);
  return normalizedData.filter((d) => filteredVals.includes(d.value));
}

/**
 * Computes the visible normalized option data, dispatching across the external
 * search-term, non-searchable/trigger, custom-filter, and default paths.
 *
 * @param p Bundled searchable flags, query/term, options, and filter config.
 * @returns The filtered option data.
 */
export function computeFilteredData(p: FilterParams): SelectOption[] {
  const { searchable, searchTerm, useTriggerMode, query, options, normalizedData, filterFunction, maxResults } = p;
  if (searchTerm) return filterDataBySearchTerm(searchTerm, normalizedData);
  if (!searchable || useTriggerMode) return normalizedData;
  if (filterFunction) return filterDataByCustom(filterFunction, query, options, normalizedData);
  return filterDataByQuery(query, normalizedData, maxResults);
}
